import clsx from "clsx";
import React, { FC, useMemo, useState } from "react";
import {
  BalanceAccount,
  MsgSend,
  networkLabel,
  validateAddress,
} from "rujira.js";
import { Button, Decimal, IconDenom, Input, Numeric } from "rujira.ui";
import { MinusCircle, Plus, Xmark } from "rujira.ui/src/components/icons/Icons";
import {
  BalanceCompact,
  usePreloadedBalance,
} from "../../../common/components/Balance";
import { MsgProvider, TxButton } from "../../../common/components/TxButton";

export const Send: React.FC<{
  cancel: () => void;
}> = ({ cancel }) => {
  const balance = usePreloadedBalance();
  const [account, setAccount] = useState(balance.account);
  if (!account) return null;

  return (
    <>
      <h2 className="h3 flex ai-c">
        Send {account.asset.metadata.symbol}
        <IconDenom
          denom={account.asset.metadata.symbol}
          className="w-4 h-4 block ml-1"
        />
      </h2>

      <BalanceCompact onClick={(b) => !("accounts" in b) && setAccount(b)} />

      <SendAccount account={account} cancel={cancel} />
    </>
  );
};

export const SendAccount: React.FC<{
  account: BalanceAccount;
  cancel: () => void;
}> = ({ cancel, account }) => {
  const [recipients, setRecipients] = useState<
    Record<string, { address: string; amount: bigint }>
  >({ "0": { address: "", amount: account.balance } });
  const [memo, setMemo] = useState("");
  const [showMemo, setShowMemo] = useState(false);
  const recipient = recipients["0"];

  const msg = useMemo(() => {
    if (!recipient) return null;
    if (!account) return null;
    if (!recipient.amount) return null;
    return new MsgSend(
      {
        address: account.address,
        network:
          account.asset.type === "SECURED" ? "THOR" : account.asset.chain,
      },
      account.asset,
      recipient.amount,
      recipient.address.trim(),
      memo
    );
  }, [account, recipient, memo]);

  return (
    <MsgProvider msg={msg}>
      <Recipients
        account={account}
        recipients={recipients}
        set={(idx: string, address: string, amount: bigint) => {
          setRecipients((prev) => ({ ...prev, [idx]: { address, amount } }));
        }}
        remove={(idx) =>
          setRecipients((prev) => {
            const { [idx]: _remove, ...rest } = prev;
            return rest;
          })
        }
      />

      <div className="mt-2">
        <button
          className="transparent condensed fs-14 fw-500 color-grey hover-white iflex ai-c pointer"
          onClick={() => setShowMemo(!showMemo)}>
          {!showMemo ? (
            <>
              <Plus className="h-1.5 w-a mr-0.5" />
              Add a memo with send
            </>
          ) : (
            <>
              <Xmark className="h-1.5 w-a mr-0.5" />
              Remove memo
            </>
          )}
        </button>
        {showMemo && (
          <Input
            value={memo}
            onChange={(e) => setMemo(e.currentTarget.value)}
            className="mt-1"
            label="Memo"
          />
        )}
      </div>

      <div className="modal__footer mt-4 px-3 py-2 flex ai-c jc-e wrap">
        <Button
          className="button--grey button--outline"
          onClick={cancel}
          label="Cancel"
        />
        <div className="block ml-a text-right">
          <TxButton
            className="button ml-1"
            label="Send"
            onSuccess={() => cancel()}
          />
        </div>
      </div>
    </MsgProvider>
  );
};

const Recipients: FC<{
  account: BalanceAccount;
  recipients: Record<string, { address: string; amount: bigint }>;
  add?: () => void;
  set: (idx: string, address: string, amount: bigint) => void;
  remove: (idx: string) => void;
}> = ({ account, recipients, add, set, remove }) => {
  return (
    <>
      <div className="row pad-mini mt-1.5 condensed fs-14 color-grey">
        <div className="col-8 fw-500">
          {networkLabel(
            account.asset.type === "SECURED" ? "THOR" : account.asset.chain
          )}{" "}
          Address
        </div>
        <div className="col-4 text-right mr-0.5 fw-500">Amount</div>
      </div>
      {Object.entries(recipients).map(([idx, { address, amount }], i) => {
        const isValid = validateAddress(
          account.asset.type === "SECURED" ? "THOR" : account.asset.chain,
          address
        );

        return (
          <div className="row pad-mini mt-1" key={idx}>
            <div className="col-8 flex ai-c">
              {i > 0 && (
                <button
                  className="transparent color-red hover-white pointer mr-0.5 block"
                  onClick={() => {
                    remove(idx);
                  }}>
                  <MinusCircle className="w-3 h-3 block" />
                </button>
              )}
              <Input
                type="text"
                value={address}
                containerClassName={clsx({
                  grow: true,
                  "input-container--error": !isValid,
                })}
                onChange={(e) => {
                  set(idx, e.currentTarget.value, amount);
                }}
              />
            </div>
            <div className="col-4">
              <div className="flex ai-c">
                <Numeric
                  full
                  amount={amount}
                  onChangeAmount={(v: bigint) => {
                    set(idx, address, v);
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
      {add && (
        <div className="mt-1 flex w-full pr-0.5">
          <Button
            className="button--xs button--grey button--outline"
            disabled={!add}
            onClick={() => {
              add?.();
            }}
            label="Add Recipient">
            <Plus />
          </Button>
          <Decimal
            decimals={8}
            className="ml-a mr-0 fs-14"
            amount={Object.values(recipients).reduce(
              (a, b) => a + b.amount,
              0n
            )}
          />
        </div>
      )}
    </>
  );
};
